/**
 * B26 / WS-4 G5 — **the signed-in operator's own profile, fetched once per session and per identity.**
 *
 * # ⛔ WHY A CONTEXT AND NOT A PROP OR A SECOND `useApiResource`
 *
 * Ruled by the lead (ACK-ws4-dev-20, option C). The two alternatives both fail on facts already
 * recorded in this tree:
 * - **A prop threaded from `CpAdminApp`** would change a shape that `AuthenticatedShell` →
 *   `ShellSession` → `SectionHeader` → `UserMenu` all construct — the exact four-layer thread
 *   `UserMenu`'s own docblock argues against for `siteRoles`.
 * - **A `useApiResource` inside `UserMenu`** would make every suite that mounts the chrome issue a
 *   real `GET /v1/auth/me`, and this package's MSW server runs `onUnhandledRequest: 'error'` — so it
 *   reds 8 suites that have nothing to do with avatars.
 *
 * ⭐ The precedent is in this file's own neighbourhood: `UserMenu` already reads `siteRoles` from
 * `usePermissionSnapshot()`, a context fed once by `CpAdminApp`, for exactly this reason.
 *
 * # ⛔ THE DEFAULT IS `null` AND `useOperatorProfile` DOES **NOT** THROW OUTSIDE A PROVIDER
 *
 * ⚠️ **This deliberately differs from its sibling `useAppsContext`, which throws.** The difference is
 * the consequence of being wrong: an absent app list decides *navigation* (so failing loudly is
 * right — a silent `[]` routes a principal to the zero-access panel), whereas an absent profile
 * decides only whether a photo or a monogram renders. `UserMenu` is mounted by five suites and by the
 * login-adjacent chrome; making the identity control **crash** without a provider would be a far
 * worse failure than falling back to the monogram it renders today. Same argument, same shape, as
 * `EMPTY_PERMISSION_SNAPSHOT`.
 *
 * ⇒ **outside a provider: the fallback, not a crash and not a network call.**
 *
 * # ⛔⛔ THE PROFILE IS TAGGED WITH THE IDENTITY IT WAS FETCHED FOR
 *
 * ⚠️ Copied deliberately from `apps-context.tsx`, and it matters MORE here. React flushes passive
 * effects **after** the commit, so an untagged store renders the **previous operator's face and email**
 * for one commit after an identity change. A stale app list is a wrong menu; a stale avatar is an
 * operator looking at someone else's identity in the control plane's own chrome — and the whole point
 * of showing a face is that a human trusts it faster than they read the email beside it.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getOperatorProfile, type OperatorProfile } from '../api/profile.api';

/**
 * The profile that may be SHOWN, or `null`.
 *
 * ⛔ **One `null`, three causes, and they are deliberately NOT distinguished** — not yet arrived, the
 * read failed, or no operator row exists for a validly-signed token (the handler's own `return null`).
 * Every one of them renders the same monogram, which is the honest answer in all three: *we are not
 * claiming a photo*. ⚠️ This is why there is no `error` field here, unlike `AppsListState`'s three-state
 * discipline — nothing downstream branches on the reason, so carrying it would invite a future author
 * to render an errorstate for a missing profile photo.
 */
export type ProfileState = OperatorProfile | null;

/** ⛔ Tagged, so a profile fetched for operator A is not shown to operator B. See the header. */
interface FetchedProfile {
  readonly forOperatorId: string | null;
  readonly profile: OperatorProfile | null;
}

/**
 * Which profile may be shown. A **render-time** derivation, and it must stay one: the equivalent
 * "clear the stored value from an effect" shape always leaves one commit holding the previous
 * operator's identity, which is precisely the defect the tag exists to prevent.
 */
export function visibleProfile(
  isAuthenticated: boolean,
  operatorId: string | null,
  fetched: FetchedProfile | null,
): ProfileState {
  if (!isAuthenticated) return null;
  if (fetched === null || fetched.forOperatorId !== operatorId) return null;
  return fetched.profile;
}

const ProfileCtx = createContext<ProfileState>(null);

export interface ProfileProviderProps {
  readonly isAuthenticated: boolean;
  readonly operatorId: string | null;
  readonly children: ReactNode;
}

export function ProfileProvider({
  isAuthenticated,
  operatorId,
  children,
}: ProfileProviderProps): React.JSX.Element {
  const [fetched, setFetched] = useState<FetchedProfile | null>(null);
  const profile = visibleProfile(isAuthenticated, operatorId, fetched);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let live = true;
    // Captured, NOT read in the callback: the tag must be the identity the request was issued FOR.
    const forOperatorId = operatorId;
    getOperatorProfile()
      .then((next) => {
        if (live) setFetched({ forOperatorId, profile: next });
      })
      .catch(() => {
        /*
         * ⛔ SWALLOWED ON PURPOSE, and this is the one place in the console where that is right.
         * A failed profile read costs a photo. Surfacing it would put an error banner on the chrome
         * of every screen for a cosmetic degradation — and `displayMessage` is not even reachable
         * from here, because there is no surface to show it on. ⚠️ The honest signal already exists
         * and is queryable: `cp_auth.operators.avatar_url`.
         */
        if (live) setFetched({ forOperatorId, profile: null });
      });
    return () => {
      live = false;
    };
    // Re-fetch on identity change: a different operator is a different face and a different email.
  }, [isAuthenticated, operatorId]);

  return <ProfileCtx value={profile}>{children}</ProfileCtx>;
}

/**
 * The signed-in operator's profile, or `null`.
 *
 * ⛔ Does NOT throw outside a provider — see the module header. `null` ⇒ render the monogram.
 */
export function useOperatorProfile(): ProfileState {
  return useContext(ProfileCtx);
}
